// apps/widget/src/lib/facemesh.ts
//
// MediaPipe FaceMesh: the live guidance ring, the pose check, and the region
// polygons the report overlay and the simulation masks both reuse.
//
// Loaded ahead of the camera, not with it. The runtime (~11 MB of WASM) and
// the model (~3.7 MB) used to start downloading only once the camera was on,
// from two other hosts; on a phone the guidance signals then appeared about
// ten seconds after the face, and the scanner looked broken. Now:
//   - preloadFaceMesh() starts it on the screens before the camera, while the
//     visitor reads (it is not in the initial bundle, so the welcome screen
//     stays light);
//   - both files come from the widget's own origin, cached for a year at
//     versioned paths, the runtime matching the installed package exactly;
//   - a phone whose GPU path fails falls back to the CPU instead of giving up.

import type { FaceRegion } from '@skinvizo/shared';

export interface FaceGeometry {
  faceCount: number;
  /** Face box height as a fraction of frame height. */
  heightFraction: number;
  /** Degrees. Positive yaw = turned to their left. */
  yaw: number;
  pitch: number;
  roll: number;
  box: { x: number; y: number; width: number; height: number };
  landmarks: { x: number; y: number }[];
}

interface Landmarker {
  detectForVideo(source: HTMLVideoElement | HTMLCanvasElement, timestamp: number): {
    faceLandmarks: { x: number; y: number; z: number }[][];
  };
  close(): void;
}

let landmarker: Landmarker | null = null;
let loading: Promise<Landmarker | null> | null = null;

const WASM_PATH = new URL(`/mediapipe/wasm-${__MEDIAPIPE_VERSION__}`, location.origin).toString();
const MODEL_PATH = new URL('/mediapipe/models/face_landmarker-float16-v1.task', location.origin).toString();

export async function loadFaceMesh(): Promise<boolean> {
  if (landmarker) return true;

  loading ??= (async () => {
    try {
      const vision = await import('@mediapipe/tasks-vision');
      const fileset = await vision.FilesetResolver.forVisionTasks(WASM_PATH);
      const create = (delegate: 'GPU' | 'CPU') =>
        vision.FaceLandmarker.createFromOptions(fileset, {
          baseOptions: { modelAssetPath: MODEL_PATH, delegate },
          runningMode: 'VIDEO',
          numFaces: 2, // 2, not 1 — we must be able to SAY "more than one face"
          outputFaceBlendshapes: false,
        });
      // GPU is faster per frame, but some phones' WebGL setup fails; the CPU
      // path is slower but works there.
      const instance = await create('GPU').catch(() => create('CPU'));
      return instance as unknown as Landmarker;
    } catch {
      // Guidance degrades rather than blocking: the server gate still runs, and
      // a visitor on a browser without WASM support can still scan.
      return null;
    }
  })();

  landmarker = await loading;
  return landmarker !== null;
}

/**
 * Starts loading the face model in the background, on the screens before the
 * camera, so it is ready when the camera opens. Safe to call repeatedly.
 */
export function preloadFaceMesh(): void {
  void loadFaceMesh();
}

export function isFaceMeshReady(): boolean {
  return landmarker !== null;
}

/**
 * Landmarks for a live video frame, or for a captured canvas. The capture path
 * passes the canvas so the points belong to the photo itself — not to a frame
 * from before the countdown, which put marks wherever the face used to be.
 */
export function detect(
  source: HTMLVideoElement | HTMLCanvasElement,
  timestamp: number,
): FaceGeometry | null {
  const width = source instanceof HTMLVideoElement ? source.videoWidth : source.width;
  if (!landmarker || width === 0) return null;

  const result = landmarker.detectForVideo(source, timestamp);
  const faces = result.faceLandmarks;
  if (!faces?.length) return null;

  const landmarks = faces[0]!;
  const xs = landmarks.map((p) => p.x);
  const ys = landmarks.map((p) => p.y);

  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  return {
    faceCount: faces.length,
    heightFraction: maxY - minY,
    ...estimatePose(landmarks),
    box: { x: minX, y: minY, width: maxX - minX, height: maxY - minY },
    landmarks: landmarks.map((p) => ({ x: p.x, y: p.y })),
  };
}

/**
 * Pose from landmark geometry rather than a solvePnP.
 *
 * Approximate, and deliberately so — this drives a guidance ring at 30fps, and
 * a full pose solve per frame costs more than the accuracy is worth. The
 * thresholds in QUALITY_GATE are tuned against these estimates.
 */
function estimatePose(landmarks: { x: number; y: number; z: number }[]): {
  yaw: number;
  pitch: number;
  roll: number;
} {
  const noseTip = landmarks[1];
  const leftEye = landmarks[33];
  const rightEye = landmarks[263];
  const chin = landmarks[152];
  const forehead = landmarks[10];

  if (!noseTip || !leftEye || !rightEye || !chin || !forehead) {
    return { yaw: 0, pitch: 0, roll: 0 };
  }

  // Roll: the angle of the eye line off horizontal. Exact, unlike the others.
  const roll = (Math.atan2(rightEye.y - leftEye.y, rightEye.x - leftEye.x) * 180) / Math.PI;

  // Yaw: how far the nose sits from the midpoint of the eyes, scaled by eye
  // separation so it is independent of distance from the camera.
  const eyeMidX = (leftEye.x + rightEye.x) / 2;
  const eyeSpan = Math.abs(rightEye.x - leftEye.x) || 0.0001;
  const yaw = ((noseTip.x - eyeMidX) / eyeSpan) * 90;

  // Pitch: where the nose falls between forehead and chin. 0.5 is level.
  const faceHeight = Math.abs(chin.y - forehead.y) || 0.0001;
  const pitch = ((noseTip.y - forehead.y) / faceHeight - 0.5) * 90;

  return { yaw, pitch, roll };
}

/**
 * Landmark indices per region. The same enum drives the report overlay and the
 * simulation masks, so adding a region means adding a polygon here.
 */
export const REGION_LANDMARKS: Partial<Record<FaceRegion, number[]>> = {
  // Indices follow MediaPipe's named face-mesh contours (silhouette, eyebrows,
  // eye rings, lips). "Left" and "right" are the SUBJECT's, as in the prompt:
  // on an unmirrored photo the subject's left is on the image's right, which
  // is MediaPipe's 2xx–4xx side.

  // Mid-forehead down to the brows. Not up to the hairline: a fringe covers
  // the top of the forehead on many faces, and a mark there sat on hair.
  forehead: [108, 69, 104, 68, 71, 151, 337, 299, 333, 298, 301, 300, 293, 334, 296, 336, 9, 107, 66, 105, 63, 70],
  // Between the brows, above the nose bridge.
  glabella: [107, 55, 8, 285, 336, 9],
  // Bridge to tip, wings included.
  nose: [168, 6, 197, 195, 5, 4, 1, 19, 94, 2, 98, 327, 64, 294, 49, 279, 48, 278],
  // Under the eye ring down to the nasolabial line, out to the cheekbone.
  leftCheek: [345, 346, 347, 348, 349, 329, 371, 266, 425, 411, 376, 352, 280, 330],
  rightCheek: [116, 117, 118, 119, 120, 100, 142, 36, 205, 187, 147, 123, 50, 101],
  // Under each eye, from the lash line down — never over the eye itself,
  // which is where dark circles were drawn before.
  periorbital: [
    130, 25, 110, 24, 23, 22, 26, 112, 243, 226, 31, 228, 229, 230, 231, 232, 233, 244,
    359, 255, 339, 254, 253, 252, 256, 341, 463, 446, 261, 448, 449, 450, 451, 452, 453, 464,
  ],
  // One band lower: the hollow toward the cheek, where tear troughs sit.
  infraorbital: [
    226, 31, 228, 229, 230, 231, 232, 233, 244, 143, 111, 117, 118, 119, 120, 121, 128, 245,
    446, 261, 448, 449, 450, 451, 452, 453, 464, 372, 340, 346, 347, 348, 349, 350, 357, 465,
  ],
  upperEyelid: [
    33, 246, 161, 160, 159, 158, 157, 173, 133, 247, 30, 29, 27, 28, 56, 190,
    263, 466, 388, 387, 386, 385, 384, 398, 362, 467, 260, 259, 257, 258, 286, 414,
  ],
  lowerEyelid: [
    130, 25, 110, 24, 23, 22, 26, 112, 243, 226, 31, 228, 229, 230, 231, 232, 233, 244,
    359, 255, 339, 254, 253, 252, 256, 341, 463, 446, 261, 448, 449, 450, 451, 452, 453, 464,
  ],
  // Nose wing to mouth corner, each side.
  nasolabial: [98, 64, 203, 206, 216, 186, 92, 165, 327, 294, 423, 426, 436, 410, 322, 391],
  // Lips and the skin just around them.
  perioral: [
    61, 185, 40, 39, 37, 0, 267, 269, 270, 409, 291, 375, 321, 405, 314, 17, 84, 181, 91, 146,
    164, 57, 287, 18,
  ],
  chin: [152, 148, 176, 140, 32, 208, 199, 428, 262, 369, 400, 377],
  jawline: [172, 136, 150, 149, 176, 148, 152, 377, 400, 378, 379, 365],
  // The face oval. Moisture applies everywhere; with no polygon it drew nothing.
  fullFace: [
    10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377,
    152, 148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109,
  ],
};

/** Region polygon in normalised 0-1 coordinates, for the annotated overlay. */
export function regionPolygon(
  geometry: FaceGeometry,
  region: FaceRegion,
): { x: number; y: number }[] | null {
  const indices = REGION_LANDMARKS[region];
  if (!indices) return null;

  const points = indices
    .map((i) => geometry.landmarks[i])
    .filter((p): p is { x: number; y: number } => p !== undefined);

  return points.length >= 3 ? points : null;
}

export function disposeFaceMesh(): void {
  landmarker?.close();
  landmarker = null;
  loading = null;
}
